/**
 * Comprehensive course debugging tool
 */
import supabase from '@/lib/supabase';

interface DebugStep {
  step: string;
  status: 'success' | 'error' | 'info';
  data?: any;
  timing?: number;
}

export async function debugCourseAccess(email: string): Promise<DebugStep[]> {
  const steps: DebugStep[] = [];
  const startTime = Date.now();
  
  console.log(`🔍 === COURSE ACCESS DEBUG FOR: ${email} ===`);
  
  try {
    // Step 1: Check auth session
    const sessionStart = Date.now();
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    steps.push({
      step: 'Auth Session Check',
      status: session ? 'success' : 'error',
      data: {
        hasSession: !!session,
        sessionEmail: session?.user?.email,
        sessionUserId: session?.user?.id,
        matchesRequestedEmail: session?.user?.email === email,
        error: sessionError?.message
      },
      timing: Date.now() - sessionStart
    });
    
    // Step 2: Check employee record
    const employeeStart = Date.now();
    const { data: employeeData, error: employeeError } = await supabase
      .from('employees')
      .select('*')
      .eq('email', email)
      .single();
      
    steps.push({
      step: 'Employee Record',
      status: employeeData ? 'success' : 'error',
      data: {
        found: !!employeeData,
        employeeId: employeeData?.id,
        userId: employeeData?.user_id,
        email: employeeData?.email,
        active: employeeData?.active,
        userType: employeeData?.user_type,
        language: employeeData?.language_preference,
        error: employeeError?.message,
        fullRecord: employeeData
      },
      timing: Date.now() - employeeStart
    });
    
    // Step 3: Check active courses count
    const coursesStart = Date.now();
    const { data: allCourses, error: coursesError, count: coursesCount } = await supabase
      .from('courses')
      .select('*', { count: 'exact' })
      .eq('is_active', true);
      
    steps.push({
      step: 'Active Courses Query',
      status: coursesError ? 'error' : 'success',
      data: {
        totalActiveCourses: coursesCount,
        coursesReturned: allCourses?.length || 0,
        error: coursesError?.message,
        sampleCourses: allCourses?.slice(0, 3).map(c => ({
          id: c.id,
          title: c.title,
          language: c.language,
          is_active: c.is_active
        }))
      },
      timing: Date.now() - coursesStart
    });
    
    // Step 4: Check language-filtered courses
    if (employeeData?.language_preference) {
      const langStart = Date.now();
      const { data: langCourses, error: langError, count: langCount } = await supabase
        .from('courses')
        .select('*', { count: 'exact' })
        .eq('is_active', true)
        .eq('language', employeeData.language_preference);
        
      steps.push({
        step: 'Language Filtered Courses',
        status: langError ? 'error' : 'success',
        data: {
          userLanguage: employeeData.language_preference,
          coursesInUserLanguage: langCount,
          error: langError?.message
        },
        timing: Date.now() - langStart
      });
    }
    
    // Step 5: Check enrollments if employee exists
    if (employeeData?.id) {
      const enrollStart = Date.now();
      const { data: enrollments, error: enrollError, count: enrollCount } = await supabase
        .from('enrollments')
        .select('*, courses!inner(title, is_active)', { count: 'exact' })
        .eq('employee_id', employeeData.id);
        
      steps.push({
        step: 'User Enrollments',
        status: enrollError ? 'error' : 'success',
        data: {
          totalEnrollments: enrollCount,
          enrollmentDetails: enrollments?.map(e => ({
            courseId: e.course_id,
            courseTitle: e.courses?.title,
            courseActive: e.courses?.is_active,
            status: e.status,
            progress: e.progress
          })),
          error: enrollError?.message
        },
        timing: Date.now() - enrollStart
      });
    }
    
    // Step 6: Test direct SQL query
    const sqlStart = Date.now();
    const { data: sqlData, error: sqlError } = await supabase.rpc('get_user_courses_debug', {
      user_email: email
    }).single();
    
    if (!sqlError || sqlError.code !== 'PGRST116') { // Ignore "function does not exist" error
      steps.push({
        step: 'Direct SQL Query Test',
        status: sqlError ? 'error' : 'info',
        data: {
          note: 'This tests if a custom SQL function exists',
          result: sqlData,
          error: sqlError?.message
        },
        timing: Date.now() - sqlStart
      });
    }
    
    // Step 7: Check RLS policies
    const rlsStart = Date.now();
    const { data: rlsTest, error: rlsError } = await supabase
      .from('courses')
      .select('id')
      .limit(1);
      
    steps.push({
      step: 'RLS Policy Test',
      status: rlsError ? 'error' : 'success',
      data: {
        canReadCourses: !rlsError,
        rowsReturned: rlsTest?.length || 0,
        error: rlsError?.message
      },
      timing: Date.now() - rlsStart
    });
    
    // Step 8: Compare with working user
    const compareEmail = 'test@dashcode.com'; // A known working user
    const compareStart = Date.now();
    const { data: compareEmployee } = await supabase
      .from('employees')
      .select('*')
      .eq('email', compareEmail)
      .single();
      
    if (compareEmployee) {
      steps.push({
        step: 'Comparison with Working User',
        status: 'info',
        data: {
          workingUser: compareEmail,
          differences: {
            active: `Current: ${employeeData?.active}, Working: ${compareEmployee.active}`,
            userType: `Current: ${employeeData?.user_type}, Working: ${compareEmployee.user_type}`,
            hasUserId: `Current: ${!!employeeData?.user_id}, Working: ${!!compareEmployee.user_id}`,
            language: `Current: ${employeeData?.language_preference}, Working: ${compareEmployee.language_preference}`
          }
        },
        timing: Date.now() - compareStart
      });
    }
    
  } catch (error) {
    steps.push({
      step: 'Unexpected Error',
      status: 'error',
      data: {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      }
    });
  }
  
  // Summary
  const totalTime = Date.now() - startTime;
  const issues = steps.filter(s => s.status === 'error');
  
  console.log('\n📊 === DEBUG SUMMARY ===');
  console.log(`Total Time: ${totalTime}ms`);
  console.log(`Total Steps: ${steps.length}`);
  console.log(`Issues Found: ${issues.length}`);
  
  if (issues.length > 0) {
    console.log('\n❌ Issues:');
    issues.forEach(issue => {
      console.log(`- ${issue.step}: ${JSON.stringify(issue.data)}`);
    });
  }
  
  console.log('\n📋 Full Debug Data:');
  steps.forEach(step => {
    const icon = step.status === 'success' ? '✅' : step.status === 'error' ? '❌' : 'ℹ️';
    console.log(`\n${icon} ${step.step} (${step.timing || 0}ms):`);
    console.log(JSON.stringify(step.data, null, 2));
  });
  
  return steps;
}

// Export for console use
if (typeof window !== 'undefined') {
  (window as any).debugCourseAccess = debugCourseAccess;
}

// Helper to compare two users side by side
export async function compareUsers(email1: string, email2: string) {
  console.log(`\n🔄 === COMPARING USERS ===`);
  console.log(`User 1: ${email1}`);
  console.log(`User 2: ${email2}`);
  
  const [debug1, debug2] = await Promise.all([
    debugCourseAccess(email1),
    debugCourseAccess(email2)
  ]);
  
  console.log('\n📊 === COMPARISON RESULTS ===');
  
  // Find differences
  debug1.forEach((step1, index) => {
    const step2 = debug2[index];
    if (step1.status !== step2.status) {
      console.log(`\n⚠️ Difference in ${step1.step}:`);
      console.log(`  ${email1}: ${step1.status} - ${JSON.stringify(step1.data)}`);
      console.log(`  ${email2}: ${step2.status} - ${JSON.stringify(step2.data)}`);
    }
  });
}

if (typeof window !== 'undefined') {
  (window as any).compareUsers = compareUsers;
}